import { Service, computed, inject, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, firstValueFrom, map } from "rxjs";
import { SessionDataService } from "../../services/shared/SessionDataService.service";
import { HelpControllerService } from "../../services/server/HelpController.service";
import { CHATBOT_SCREENS } from "../chatbot/chatbot-screens.config";
import { HELP_MEDIA_API_ENABLED, HELP_TOPICS } from "./help-topics.config";
import { HelpGuide, HelpLanguage, HelpMediaItem, HelpResource, HelpTopic } from "./help.types";
import { buildHelpResources } from "./help-resources.util";
import { findHelpTopicForUrl, parseHelpGuide, resolveHelpLanguage, visibleHelpTopics } from "./help.util";

/** Help panel state and guide loading; the guide shown follows the current route unless opened explicitly. */
@Service()
export class HelpService {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly SessionDataService = inject(SessionDataService);
  private readonly HelpController = inject(HelpControllerService);

  private readonly guideCache = new Map<string, Promise<HelpGuide | null>>();

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  /** Guide language derived from the UI language. */
  readonly lang = toSignal(this.translate.onLangChange.pipe(map(e => resolveHelpLanguage(e.lang))), {
    initialValue: resolveHelpLanguage(this.translate.currentLang)
  });

  /** Topic of the current screen for the current profile. */
  readonly currentTopic = computed<HelpTopic | null>(() =>
    findHelpTopicForUrl(this.url(), this.SessionDataService.profile(), HELP_TOPICS, CHATBOT_SCREENS)
  );

  readonly isOpen = signal(false);
  private readonly explicitTopicId = signal<string | null>(null);
  /** Section to scroll to once the guide renders. */
  readonly targetSectionId = signal<string | null>(null);

  /** Global library mode: all guides listed/searchable instead of the current screen's guide. */
  readonly libraryActive = signal(false);
  /** Library list is shown (library mode without a guide selected). */
  readonly showLibrary = computed(() => this.libraryActive() && !this.explicitTopicId());

  readonly topic = computed<HelpTopic | null>(() => {
    const id = this.explicitTopicId();
    if (id) {
      return HELP_TOPICS.find(t => t.id === id) ?? null;
    }
    return this.libraryActive() ? null : this.currentTopic();
  });

  /** Guides of every topic the profile can open, loaded only while the library is shown. */
  readonly libraryGuides = resource({
    params: () =>
      this.isOpen() && this.showLibrary()
        ? { profile: this.SessionDataService.profile(), lang: this.lang() }
        : undefined,
    loader: async ({ params }) => {
      const topics = visibleHelpTopics(params.profile, HELP_TOPICS);
      const loaded = await Promise.all(
        topics.map(async topic => ({ topic, guide: await this.loadGuide(topic, params.lang) }))
      );
      return loaded;
    }
  });

  readonly guide = resource({
    params: () => {
      const topic = this.topic();
      return this.isOpen() && topic ? { topic, lang: this.lang() } : undefined;
    },
    loader: ({ params }) => this.loadGuide(params.topic, params.lang)
  });

  /** Resources table of the shown guide (id, folder, file name) to resolve backend files. */
  readonly resources = computed<HelpResource[]>(() => {
    const topic = this.topic();
    const guide = this.guide.value();
    return topic && guide ? buildHelpResources(topic, guide) : [];
  });

  readonly media = resource({
    params: () => {
      const topic = this.topic();
      return HELP_MEDIA_API_ENABLED && this.isOpen() && topic ? topic.id : undefined;
    },
    loader: ({ params }) => this.loadMediaList(params)
  });

  /** Opens the panel on a topic/section, or on the current screen's guide when omitted. */
  open(topicId?: string | null, sectionId?: string | null): void {
    this.libraryActive.set(false);
    this.explicitTopicId.set(topicId ?? null);
    this.targetSectionId.set(sectionId ?? null);
    this.isOpen.set(true);
  }

  /** Opens the global help: searchable repository of every guide. */
  openLibrary(): void {
    this.libraryActive.set(true);
    this.explicitTopicId.set(null);
    this.targetSectionId.set(null);
    this.isOpen.set(true);
  }

  /** Opens a guide from the library, keeping the way back to the list. */
  openFromLibrary(topicId: string, sectionId?: string | null): void {
    this.explicitTopicId.set(topicId);
    this.targetSectionId.set(sectionId ?? null);
  }

  backToLibrary(): void {
    this.explicitTopicId.set(null);
    this.targetSectionId.set(null);
  }

  close(): void {
    this.isOpen.set(false);
    this.libraryActive.set(false);
    this.explicitTopicId.set(null);
    this.targetSectionId.set(null);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Cached, validated guide; null when the chunk fails to load or is malformed. */
  loadGuide(topic: HelpTopic, lang: HelpLanguage): Promise<HelpGuide | null> {
    const key = `${topic.id}|${lang}`;
    let pending = this.guideCache.get(key);
    if (!pending) {
      pending = topic.guides[lang]()
        .then(m => parseHelpGuide(m.default))
        .catch(() => null);
      this.guideCache.set(key, pending);
    }
    return pending;
  }

  private async loadMediaList(topicId: string): Promise<HelpMediaItem[]> {
    try {
      const response = await firstValueFrom(this.HelpController.getMediaList(topicId));
      const body = (response as { code?: number; body?: unknown })?.body;
      return (response as { code?: number })?.code === 0 && Array.isArray(body) ? (body as HelpMediaItem[]) : [];
    } catch {
      return [];
    }
  }
}
